import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useServices } from '@/data/useServices';

export const InteractiveServices = () => {
  const { services: SERVICES } = useServices();
  const [activeIdx, setActiveIdx] = useState(0);
  const safeIdx = Math.min(activeIdx, Math.max(0, SERVICES.length - 1));
  const active = SERVICES[safeIdx] || SERVICES[0];

  if (!active) return null;

  const textColor = active.textColor || '#FFFFFF';
  const isLightText = textColor === '#FFFFFF';

  return (
    <section
      id="servicios-interactivos"
      className="relative w-full overflow-hidden"
      data-testid="home-services-interactive-section"
    >
      {/* Animated full-bleed background image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.slug}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${active.bgImage})` }}
        />
      </AnimatePresence>

      {/* Subtle gradient overlay for readability */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isLightText
            ? 'linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.28) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.14) 100%)',
        }}
      />

      <div
        className="relative container-link py-14 sm:py-16 lg:py-20"
        style={{ color: textColor }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.2fr)] gap-y-10 gap-x-8 xl:gap-x-12 items-start">
          {/* COL 1: SERVICIOS title */}
          <div className="flex lg:items-center lg:self-stretch pl-[5%]">
            <h2
              className="font-black uppercase leading-[0.85] whitespace-nowrap"
              style={{
                color: textColor,
                fontSize: 'clamp(2.25rem, 3.4vw, 3.75rem)',
                letterSpacing: '-0.04em',
              }}
              data-testid="services-section-title"
            >
              Servicios
            </h2>
          </div>

          {/* COL 2: Menu */}
          <nav
            className="flex flex-col gap-6 sm:gap-7 lg:pt-2"
            role="tablist"
            aria-label="Servicios"
          >
            {SERVICES.map((s, idx) => {
              const isActive = idx === activeIdx;
              const normalizedName = String(s.name || '').normalize('NFC');

              return (
                <button
                  key={s.slug}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveIdx(idx)}
                  className="text-left focus:outline-none w-fit max-w-full group"
                  style={{ color: textColor }}
                  data-testid={`service-tab-${s.slug}`}
                >
                  {isActive ? (
                    <span
                      className="font-wild block leading-[1.02]"
                      style={{
                        color: textColor,
                        fontSize: 'clamp(1.575rem, 2.94vw, 2.45rem)',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSynthesis: 'none',
                        textDecoration: 'underline',
                        textDecorationColor: '#ED6B24',
                        textDecorationThickness: '3px',
                        textUnderlineOffset: '8px',
                      }}
                    >
                      {normalizedName}
                    </span>
                  ) : (
                    <span
                      className="block leading-tight transition-opacity duration-200 opacity-80 group-hover:opacity-100"
                      style={{
                        fontFamily: 'Gotham, sans-serif',
                        fontWeight: 500,
                        color: textColor,
                        fontSize: 'clamp(0.875rem, 1.26vw, 1.155rem)',
                      }}
                    >
                      {normalizedName}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* COL 3: Description + CTA */}
          <div className="flex flex-col lg:pt-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${active.slug}-text`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col"
              >
                <h3
                  className="font-black leading-[1.05] mb-12"
                  style={{
                    color: textColor,
                    fontSize: 'clamp(1.594rem, 2.55vw, 2.34rem)',
                  }}
                >
                  ¿Qué hacemos?
                </h3>

                <div
                  className="leading-[1.55] max-w-xl"
                  style={{
                    color: textColor,
                    opacity: 0.95,
                    fontSize: 'clamp(0.85rem, 0.978vw, 1.02rem)',
                  }}
                >
                  <div className="[&_p:last-child]:mb-0">
                    {(active.homeIntro || '')
                      .split(/\r?\n/)
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line, i) => (
                        <p key={`intro-${i}`} className="mb-4">
                          {line}
                        </p>
                      ))}

                    {(active.homeBody || []).map((line, i) => (
                      <p key={i} className="mb-2">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="pt-3 lg:pt-4">
                  <Link
                    to={`/servicios/${active.slug}`}
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#58677A] rounded-full px-7 py-3 shadow-[0_18px_40px_rgba(0,0,0,0.18)] hover:-translate-y-0.5 hover:shadow-[0_24px_60px_rgba(0,0,0,0.25)] transition-all duration-200"
                    data-testid={`service-panel-cta-link-${active.slug}`}
                    style={{
                      fontFamily: 'Gotham, sans-serif',
                      fontWeight: 700,
                      fontSize: '11.5px',
                      minWidth: '210px',
                    }}
                  >
                    Ver más
                    <ArrowRight size={12} />
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
